import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import { sentryCreateBrowserRouter } from 'lib/sentry';
import { lazy, Suspense } from 'react';
import { Navigate, Outlet } from 'react-router';
import { MobileShell } from '../components/layout/MobileShell';
import { AuthGuard } from './AuthGuard';

// 各画面を lazy import に分割してコード分割（初回ロード軽減）。
// 名前付き export を default export 形式に変換するヘルパ。
const lazyNamed = <K extends string>(
  factory: () => Promise<Record<K, React.ComponentType<unknown>>>,
  name: K,
) => lazy(() => factory().then((m) => ({ default: m[name] })));

const LoginScreen = lazyNamed(() => import('../screens/auth/LoginScreen'), 'LoginScreen');
const ChannelDetailScreen = lazyNamed(
  () => import('../screens/channels/ChannelDetailScreen'),
  'ChannelDetailScreen',
);
const DatapointDetailScreen = lazyNamed(
  () => import('../screens/datapoints/DatapointDetailScreen'),
  'DatapointDetailScreen',
);
const DatapointTableScreen = lazyNamed(
  () => import('../screens/datapoints/DatapointTableScreen'),
  'DatapointTableScreen',
);
const DatapointsHomeScreen = lazyNamed(
  () => import('../screens/datapoints/DatapointsHomeScreen'),
  'DatapointsHomeScreen',
);
const LocationDetailScreen = lazyNamed(
  () => import('../screens/locations/LocationDetailScreen'),
  'LocationDetailScreen',
);
const LocationPickerScreen = lazyNamed(
  () => import('../screens/locations/LocationPickerScreen'),
  'LocationPickerScreen',
);
const AboutScreen = lazyNamed(() => import('../screens/settings/AboutScreen'), 'AboutScreen');
const ManualScreen = lazyNamed(() => import('../screens/settings/ManualScreen'), 'ManualScreen');
const ProfileScreen = lazyNamed(() => import('../screens/settings/ProfileScreen'), 'ProfileScreen');
const RequestScreen = lazyNamed(() => import('../screens/settings/RequestScreen'), 'RequestScreen');
const SettingsScreen = lazyNamed(
  () => import('../screens/settings/SettingsScreen'),
  'SettingsScreen',
);
const SetupHomeScreen = lazyNamed(
  () => import('../screens/setup/SetupHomeScreen'),
  'SetupHomeScreen',
);
const SetupBindScreen = lazyNamed(
  () => import('../screens/setup/SetupBindScreen'),
  'SetupBindScreen',
);
const SetupRegistrationsScreen = lazyNamed(
  () => import('../screens/setup/SetupRegistrationsScreen'),
  'SetupRegistrationsScreen',
);
const InstallationCheckScreen = lazyNamed(
  () => import('../screens/installation/InstallationCheckScreen'),
  'InstallationCheckScreen',
);
const DataConfirmScreen = lazyNamed(
  () => import('../screens/qr/DataConfirmScreen'),
  'DataConfirmScreen',
);
const DataTesterPayScreen = lazyNamed(
  () => import('../screens/qr/DataTesterPayScreen'),
  'DataTesterPayScreen',
);
const NotFoundScreen = lazyNamed(() => import('../screens/error/NotFoundScreen'), 'NotFoundScreen');
const UnexpectedErrorScreen = lazyNamed(
  () => import('../screens/error/UnexpectedErrorScreen'),
  'UnexpectedErrorScreen',
);
const ToolboxHomeScreen = lazyNamed(
  () => import('../screens/toolbox/ToolboxHomeScreen'),
  'ToolboxHomeScreen',
);
const UnitDetailScreen = lazyNamed(
  () => import('../screens/units/UnitDetailScreen'),
  'UnitDetailScreen',
);
const UnitEditScreen = lazyNamed(() => import('../screens/units/UnitEditScreen'), 'UnitEditScreen');
const UnitListScreen = lazyNamed(() => import('../screens/units/UnitListScreen'), 'UnitListScreen');

/**
 * lazy chunk のダウンロード中に出す薄いプログレスバー。
 * 各画面側で別途 loading state を持っているため、ここはシンプルに済ませる。
 */
const RouteSuspense = ({ children }: { children: React.ReactNode }) => (
  <Suspense
    fallback={
      <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}>
        <LinearProgress />
      </Box>
    }
  >
    {children}
  </Suspense>
);

const router = sentryCreateBrowserRouter([
  {
    path: '/login',
    element: (
      <RouteSuspense>
        <LoginScreen />
      </RouteSuspense>
    ),
  },
  {
    path: '/',
    element: (
      <AuthGuard>
        <RouteSuspense>
          <Outlet />
        </RouteSuspense>
      </AuthGuard>
    ),
    errorElement: (
      <RouteSuspense>
        <UnexpectedErrorScreen />
      </RouteSuspense>
    ),
    children: [
      { index: true, element: <Navigate to='/locations' replace /> },
      { path: 'locations', element: <LocationPickerScreen /> },
      {
        path: 'locations/:locationId',
        element: <MobileShell />,
        children: [
          { index: true, element: <Navigate to='units' replace /> },
          { path: 'units', element: <UnitListScreen /> },
          { path: 'units/:unitId', element: <UnitDetailScreen /> },
          { path: 'units/:unitId/edit', element: <UnitEditScreen /> },
          { path: 'units/:unitId/channels/:channelId', element: <ChannelDetailScreen /> },
          { path: 'detail', element: <LocationDetailScreen /> },
          { path: 'units/:unitId/datapoints/:datapointId', element: <DatapointDetailScreen /> },
          {
            path: 'units/:unitId/datapoints/:datapointId/table',
            element: <DatapointTableScreen />,
          },
          { path: 'datapoints', element: <DatapointsHomeScreen /> },
          { path: 'datapoints/:datapointId', element: <DatapointDetailScreen /> },
          { path: 'toolbox', element: <ToolboxHomeScreen /> },
          { path: 'setup', element: <SetupHomeScreen /> },
          { path: 'setup/bind', element: <SetupBindScreen /> },
          { path: 'setup/registrations', element: <SetupRegistrationsScreen /> },
          { path: 'installation', element: <InstallationCheckScreen /> },
          { path: 'qr', element: <DataTesterPayScreen /> },
          { path: 'qr/data/:unitId', element: <DataConfirmScreen /> },
          { path: 'settings', element: <SettingsScreen /> },
          { path: 'settings/profile', element: <ProfileScreen /> },
          { path: 'settings/about', element: <AboutScreen /> },
          // 目次ページと章ごとの個別ページ（`settings/manual/login` 等）
          { path: 'settings/manual/:slug?', element: <ManualScreen /> },
          { path: 'settings/request', element: <RequestScreen /> },
        ],
      },
      { path: '*', element: <NotFoundScreen /> },
    ],
  },
]);

export { router };
